import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { theme, Checkbox, Button } from 'antd';
import { motion } from 'framer-motion';
import { DragOutlined, StopOutlined } from '@ant-design/icons';
import {
    closestCorners,
    DndContext,
    DragOverlay,
    PointerSensor,
    useDroppable,
    useDraggable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { Container } from '@/components/ui/Container';
import { StyledLinkButton } from '@/components/ui/Link';
import {
    SprintKanbanScrollChainStyle,
    SprintKanbanDragCursorStyle,
} from '@/components/module/projects/sprints_tab/SprintKanbanBoard';
import { SprintTaskCard } from '@/components/sprint/SprintTaskCard';
import { getSprintTaskApiId } from '@hooks/projects/projectHelpers';
import { useSprintKanbanSyncHorizontalScroll } from '@hooks/projects/useProjectSprints';
import { useMatchMedia } from '@hooks/useMatchMedia';
import QASubmissionGroupCard from '@/components/module/projects/qa_tab/QASubmissionGroupCard';
import FloatingSelectionBar from '@/components/ui/FloatingSelectionBar';
import {
    SprintKanbanFill,
    KanbanThreeColumnRow,
    KanbanColumnPanel,
    KanbanColumnHeader,
    KanbanColumnTitle,
    KanbanColumnCountBadge,
    KanbanColumnBody,
    KanbanDragDropPlaceholder,
    kanbanDesktopHeaderCellFlexStyle,
    kanbanDesktopBoardRowInnerStyle,
    kanbanDesktopBoardHeaderRowInnerStyle,
} from '@/pages/inside/tenant/style';

/** Drag/drop column ids (PMO uses "pmo" here; card tint still uses kanbanColumn "submitted"). */
const COL = { TODO: 'todo', BUGS: 'bugs', RESOLVED: 'resolved', PMO: 'pmo' };

export const LoadMoreCard = styled.button`
    width: 100%;
    margin-top: 12px;
    padding: 12px;
    border-radius: 8px;
    background: var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.02));
    backdrop-filter: blur(12px);
    border: 1px dashed color-mix(in srgb, var(--ant-color-primary, #7c3aed) 30%, transparent);
    color: var(--ant-color-primary, #7c3aed);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: block;
    text-align: center;
    &:hover {
        background: color-mix(in srgb, var(--ant-color-primary, #7c3aed) 8%, transparent);
        transform: translateY(-1px);
    }
    &:active {
        transform: translateY(0);
    }
`;

const QA_COLUMN_DROP_MESSAGES = {
    [COL.TODO]: 'Drop here for TODOs',
    [COL.BUGS]: 'Drop here to report bugs (opens form)',
    [COL.RESOLVED]: 'Drop here to mark Resolved',
    [COL.PMO]: 'Drop here to submit to PMO',
};

const SPRINT_KANBAN_DRAG_CLASS = 'sprint-kanban-dragging';

/**
 * Whole-submission drag: drops on PMO open the submit modal with every task in the group.
 * Per-task drag is still handled inside {@link QASubmissionGroupCard} rows.
 */
function DraggableQaSubmissionGroupCard({ sprintId, group, groupIndex, todoPmoSelectMode, children }) {
    const submissionTasks = Array.isArray(group?.tasks) ? group.tasks : [];
    const selectedTaskIds = submissionTasks
        .map((t) => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId)
        .filter((x) => x != null && String(x).trim() !== '' && String(x) !== 'undefined')
        .map(String);

    const gid =
        group?.id ?? group?.submissionId ?? group?._id ?? `${String(sprintId ?? 's')}-qa-submission-${groupIndex}`;

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `qa-submission-${String(gid)}`,
        data: {
            type: 'submission',
            submissionId: gid,
            sprintId,
            sourceColumn: COL.TODO,
            selectedTaskIds,
            submissionSidLabel: group?.sidLabel ?? '',
            submissionTaskCount: selectedTaskIds.length,
        },
        disabled: Boolean(todoPmoSelectMode),
    });

    const style = {
        width: '100%',
        minWidth: 0,
        ...(isDragging
            ? {
                transform: 'none',
                opacity: 0.14,
                filter: 'grayscale(0.2)',
                pointerEvents: 'none',
            }
            : transform
                ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
                : {}),
    };

    const dragZoneProps = todoPmoSelectMode ? {} : { ...listeners, ...attributes };

    return (
        <div ref={setNodeRef} style={style}>
            {typeof children === 'function'
                ? children({ submissionDragZoneProps: dragZoneProps })
                : children}
        </div>
    );
}

function KanbanEmptyColumn({ title, token, hasSprint }) {
    const border = token?.colorBorderSecondary ?? 'rgba(0,0,0,0.08)';
    const bg = token?.colorFillQuaternary ?? 'rgba(0,0,0,0.02)';
    const text = token?.colorTextSecondary ?? 'rgba(0,0,0,0.55)';
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                minHeight: '100%',
                borderRadius: 12,
                border: `1px dashed ${border}`,
                background: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                boxSizing: 'border-box',
                color: text,
                textAlign: 'center',
                fontSize: 13,
                lineHeight: 1.4,
                fontFamily: 'var(--font-family-geist), system-ui, sans-serif',
            }}
        >
            <div style={{ maxWidth: 240 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, color: token?.colorText ?? 'rgba(0,0,0,0.88)' }}>
                    No tasks
                </div>
                <div>
                    {hasSprint
                        ? title
                            ? `This sprint has no tasks in ${title}.`
                            : 'This sprint has no tasks here yet.'
                        : 'No tasks here yet.'}
                </div>
            </div>
        </div>
    );
}

/**
 * QA tab: Kanban — TODOs (tasks + submission groups draggable) | Bugs reported | Submitted (PMO).
 * Desktop layout matches Sprint Kanban (sticky header strip, synced horizontal scroll, column scroll).
 */
export default function QAKanbanBoard({
    sprint,
    formatDateFn,
    todoTasks,
    todoSubmissionGroups = [],
    bugsTasks,
    resolvedTasks = [],
    pmoTasks,
    onTaskSelect,
    onSubmissionOpen,
    dropTarget,
    draggingColumn,
    onDndDragStart,
    onDndDragOverColumn,
    onDndDragEnd,
    draggingSelectedCount = 0,
    todoPmoSelectMode = false,
    todoPmoSelectedIds = [],
    onTodoPmoToggleTask,
    onTodoPmoStartSelectMode,
    onTodoPmoCancelSelectMode,
    onTodoPmoSelectAll,
    onTodoPmoUnselectAll,
    onTodoPmoSubmitAll,
    onTodoPmoSubmitSelection,
    resolvedPmoSelectMode = false,
    resolvedPmoSelectedIds = [],
    onResolvedPmoToggleTask,
    onResolvedPmoStartSelectMode,
    onResolvedPmoCancelSelectMode,
    onResolvedPmoSelectAll,
    onResolvedPmoUnselectAll,
    onResolvedPmoSubmitAll,
    onResolvedPmoSubmitSelection,
    onPmoSubmittedTaskOpenDetail,
    pmoSubmissionGroups = [],
    isTodoPmoTaskDisabled,
    isResolvedPmoTaskDisabled,
    qaSubmissions = [],
}) {
    const { token } = theme.useToken();
    const sprintId = sprint?.id;
    const hasSprint = Boolean(sprintId);
    const isNarrow = useMatchMedia('(max-width: 1024px)');
    const [todoLimit, setTodoLimit] = useState(100);
    const [bugsLimit, setBugsLimit] = useState(100);
    const [resolvedLimit, setResolvedLimit] = useState(100);
    const [pmoLimit, setPmoLimit] = useState(100);

    const filteredTodoTasks = useMemo(() => {
        if (!todoPmoSelectMode || todoPmoSelectedIds.length === 0) return todoTasks || [];
        const firstSelectedId = String(todoPmoSelectedIds[0]);
        const firstTask = (todoTasks || []).find(
            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === firstSelectedId
        );
        if (!firstTask) return todoTasks || [];

        const firstCameFromQa = Boolean(firstTask.cameFromQa || firstTask.came_from_qa);
        if (firstCameFromQa) {
            const tId = getSprintTaskApiId(firstTask) ?? firstTask?.id ?? firstTask?.taskId;
            const match = (qaSubmissions || []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            const firstSubId = match ? (match.submissionId || match.id) : null;

            return (todoTasks || []).filter((t) => {
                const currentCameFromQa = Boolean(t.cameFromQa || t.came_from_qa);
                if (!currentCameFromQa) return false;
                const currentTId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                const currentMatch = (qaSubmissions || []).find((sub) =>
                    Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(currentTId))
                );
                const currentSubId = currentMatch ? (currentMatch.submissionId || currentMatch.id) : null;
                return currentSubId === firstSubId;
            });
        } else {
            return (todoTasks || []).filter((t) => !Boolean(t.cameFromQa || t.came_from_qa));
        }
    }, [todoPmoSelectMode, todoPmoSelectedIds, todoTasks, qaSubmissions]);

    const filteredResolvedTasks = useMemo(() => {
        if (!resolvedPmoSelectMode || resolvedPmoSelectedIds.length === 0) return resolvedTasks || [];
        const firstSelectedId = String(resolvedPmoSelectedIds[0]);
        const firstTask = (resolvedTasks || []).find(
            (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === firstSelectedId
        );
        if (!firstTask) return resolvedTasks || [];

        const firstCameFromQa = Boolean(firstTask.cameFromQa || firstTask.came_from_qa);
        if (firstCameFromQa) {
            const tId = getSprintTaskApiId(firstTask) ?? firstTask?.id ?? firstTask?.taskId;
            const match = (qaSubmissions || []).find((sub) =>
                Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(tId))
            );
            const firstSubId = match ? (match.submissionId || match.id) : null;

            return (resolvedTasks || []).filter((t) => {
                const currentCameFromQa = Boolean(t.cameFromQa || t.came_from_qa);
                if (!currentCameFromQa) return false;
                const currentTId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                const currentMatch = (qaSubmissions || []).find((sub) =>
                    Array.isArray(sub?.tasks) && sub.tasks.some((tIdVal) => String(tIdVal) === String(currentTId))
                );
                const currentSubId = currentMatch ? (currentMatch.submissionId || currentMatch.id) : null;
                return currentSubId === firstSubId;
            });
        } else {
            return (resolvedTasks || []).filter((t) => !Boolean(t.cameFromQa || t.came_from_qa));
        }
    }, [resolvedPmoSelectMode, resolvedPmoSelectedIds, resolvedTasks, qaSubmissions]);

    const standalonePmoTasks = useMemo(() => {
        const groupedTaskIds = new Set();
        (pmoSubmissionGroups || []).forEach((g) => {
            (g.tasks || []).forEach((t) => {
                const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                if (tid != null) groupedTaskIds.add(String(tid));
            });
        });
        return (pmoTasks || []).filter((t) => {
            const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            return tid == null ? true : !groupedTaskIds.has(String(tid));
        });
    }, [pmoTasks, pmoSubmissionGroups]);

    const { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll } = useSprintKanbanSyncHorizontalScroll();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );

    const [activeDragPayload, setActiveDragPayload] = useState(null);

    const handleDragStart = useCallback(
        (event) => {
            const payload = event?.active?.data?.current ?? null;
            setActiveDragPayload(payload);
            onDndDragStart?.(payload);
            document.body.classList.add(SPRINT_KANBAN_DRAG_CLASS);
        },
        [onDndDragStart],
    );

    const handleDragOver = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            if (overColumnId) onDndDragOverColumn?.(String(overColumnId), payload);
        },
        [onDndDragOverColumn],
    );

    const handleDragEnd = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            onDndDragEnd?.({ overColumnId: overColumnId != null ? String(overColumnId) : null, payload });
            setActiveDragPayload(null);
            document.body.classList.remove(SPRINT_KANBAN_DRAG_CLASS);
        },
        [onDndDragEnd],
    );

    const handleDragCancel = useCallback(() => {
        setActiveDragPayload(null);
        document.body.classList.remove(SPRINT_KANBAN_DRAG_CLASS);
    }, []);

    function DroppableKanbanColumnBody({ columnId, children, ...bodyProps }) {
        /**
         * Droppable should be on the actual visible scrollport (`KanbanColumnBody`),
         * not just the panel wrapper, otherwise hit-testing can fail and `over` becomes null.
         */
        const { setNodeRef } = useDroppable({ id: columnId });
        return (
            <KanbanColumnBody ref={setNodeRef} data-kanban-drop-column={columnId} {...bodyProps}>
                {children}
            </KanbanColumnBody>
        );
    }

    function DraggableTodoCard({ task, draggableId, selectedTaskIds, dragEnabled, children }) {
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id: draggableId,
            data: {
                type: 'task',
                sprintId,
                taskId: getSprintTaskApiId(task) ?? task?.id ?? task?.taskId,
                sourceColumn: COL.TODO,
                selectedTaskIds,
            },
            disabled: !dragEnabled,
        });
        const style = transform && !isDragging
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
            : undefined;
        return (
            <div
                ref={setNodeRef}
                style={{
                    ...style,
                    opacity: isDragging ? 0 : 1,
                    width: '100%',
                    minWidth: 0,
                }}
                {...attributes}
                {...listeners}
            >
                {children}
            </div>
        );
    }

    function DraggableResolvedCard({ task, draggableId, selectedTaskIds, dragEnabled, children }) {
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id: draggableId,
            data: {
                type: 'task',
                sprintId,
                taskId: getSprintTaskApiId(task) ?? task?.id ?? task?.taskId,
                sourceColumn: COL.RESOLVED,
                selectedTaskIds,
            },
            disabled: !dragEnabled,
        });
        const style = transform && !isDragging
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
            : undefined;
        return (
            <div
                ref={setNodeRef}
                style={{
                    ...style,
                    opacity: isDragging ? 0 : 1,
                    width: '100%',
                    minWidth: 0,
                }}
                {...attributes}
                {...listeners}
            >
                {children}
            </div>
        );
    }

    const todoPmoSelectedSet = useMemo(
        () => new Set((todoPmoSelectedIds ?? []).map(String)),
        [todoPmoSelectedIds],
    );

    const resolvedPmoSelectedSet = useMemo(
        () => new Set((resolvedPmoSelectedIds ?? []).map(String)),
        [resolvedPmoSelectedIds],
    );

    const resolveDraggingTodoTask = useCallback(
        (taskId) => {
            const tid = taskId == null || taskId === '' ? null : String(taskId);
            if (!tid) return null;
            const fromFlat = (Array.isArray(todoTasks) ? todoTasks : []).find(
                (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === tid,
            );
            if (fromFlat) return fromFlat;
            for (const g of Array.isArray(todoSubmissionGroups) ? todoSubmissionGroups : []) {
                const hit = (Array.isArray(g?.tasks) ? g.tasks : []).find(
                    (x) => String(getSprintTaskApiId(x) ?? x?.id ?? x?.taskId) === tid,
                );
                if (hit) return hit;
            }
            return null;
        },
        [todoTasks, todoSubmissionGroups],
    );

    const resolveDraggingResolvedTask = useCallback(
        (taskId) => {
            const tid = taskId == null || taskId === '' ? null : String(taskId);
            if (!tid) return null;
            return (Array.isArray(resolvedTasks) ? resolvedTasks : []).find(
                (t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId) === tid,
            );
        },
        [resolvedTasks],
    );

    const resolveDraggingSubmissionGroup = useCallback(
        (submissionId) => {
            const sid = submissionId == null || submissionId === '' ? null : String(submissionId);
            if (!sid) return null;
            const groups = Array.isArray(todoSubmissionGroups) ? todoSubmissionGroups : [];
            return (
                groups.find((g) => String(g?.id ?? g?.submissionId ?? g?._id ?? '') === sid) ??
                groups.find((_g, idx) => String(idx) === sid) ??
                null
            );
        },
        [todoSubmissionGroups],
    );

    const submissionOverlay = useMemo(() => {
        if (activeDragPayload?.type !== 'submission') return null;
        const group = resolveDraggingSubmissionGroup(activeDragPayload?.submissionId);
        if (!group) {
            return {
                sidLabel: activeDragPayload?.submissionSidLabel || 'Submission',
                submissionTitle: 'Submission',
                submissionStatus: '',
                descriptionMarkdown: '',
                attachmentCount: 0,
                submittedAtLabel: null,
                submittedBy: null,
                tasks: [],
            };
        }
        const tasks = Array.isArray(group?.tasks) ? group.tasks : [];
        const preview = tasks.slice(0, 5);
        const remaining = Math.max(tasks.length - preview.length, 0);
        const previewTasks =
            remaining > 0
                ? [
                    ...preview,
                    {
                        id: '__more__',
                        taskNumber: `+${remaining} more`,
                        title: 'Open submission details to view all tasks.',
                        type: 'feature',
                        priority: 'low',
                    },
                ]
                : preview;
        return {
            sidLabel: group?.sidLabel || activeDragPayload?.submissionSidLabel || 'Submission',
            submissionTitle: group?.submissionTitle || 'Submission',
            submissionStatus: group?.statusLabel || '',
            descriptionMarkdown: group?.descriptionMarkdown || '',
            attachmentCount: group?.attachmentCount || 0,
            submittedAtLabel: group?.submittedAtLabel || null,
            submittedBy: group?.submittedBy || null,
            tasks: previewTasks,
        };
    }, [activeDragPayload, resolveDraggingSubmissionGroup]);

    const renderTodoHeaderRight = () => {
        const n = todoPmoSelectedIds.length;
        const allSelected = todoTasks.length > 0 && n === todoTasks.length;
        if (todoPmoSelectMode) {
            return (
                <div
                    style={{
                        marginLeft: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        flexShrink: 0,
                        minWidth: 0,
                    }}
                >
                    <StyledLinkButton
                        type="button"
                        aria-label={allSelected ? 'Unselect all tasks' : 'Select all tasks'}
                        onClick={() => {
                            if (allSelected) onTodoPmoUnselectAll?.();
                            else onTodoPmoSelectAll?.();
                        }}
                        style={{ fontSize: 12, lineHeight: 1.2 }}
                    >
                        {allSelected ? 'Unselect all' : 'Select all'}
                    </StyledLinkButton>
                    <StyledLinkButton
                        type="button"
                        aria-label="Cancel task selection"
                        onClick={() => onTodoPmoCancelSelectMode?.()}
                        style={{ fontSize: 12, lineHeight: 1.2 }}
                    >
                        Cancel
                    </StyledLinkButton>
                </div>
            );
        }
        return (
            <StyledLinkButton
                type="button"
                aria-label="Select tasks"
                onClick={() => onTodoPmoStartSelectMode?.()}
                style={{ marginLeft: 'auto', fontSize: 12, lineHeight: 1.2 }}
            >
                Select
            </StyledLinkButton>
        );
    };

    const renderResolvedHeaderRight = () => {
        const n = resolvedPmoSelectedIds.length;
        const allSelected = resolvedTasks.length > 0 && n === resolvedTasks.length;
        if (resolvedPmoSelectMode) {
            return (
                <div
                    style={{
                        marginLeft: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        flexShrink: 0,
                        minWidth: 0,
                    }}
                >
                    <StyledLinkButton
                        type="button"
                        aria-label={allSelected ? 'Unselect all tasks' : 'Select all tasks'}
                        onClick={() => {
                            if (allSelected) onResolvedPmoUnselectAll?.();
                            else onResolvedPmoSelectAll?.();
                        }}
                        style={{ fontSize: 12, lineHeight: 1.2 }}
                    >
                        {allSelected ? 'Unselect all' : 'Select all'}
                    </StyledLinkButton>
                    <StyledLinkButton
                        type="button"
                        aria-label="Cancel task selection"
                        onClick={() => onResolvedPmoCancelSelectMode?.()}
                        style={{ fontSize: 12, lineHeight: 1.2 }}
                    >
                        Cancel
                    </StyledLinkButton>
                </div>
            );
        }
        return (
            <StyledLinkButton
                type="button"
                aria-label="Select tasks"
                onClick={() => onResolvedPmoStartSelectMode?.()}
                style={{ marginLeft: 'auto', fontSize: 12, lineHeight: 1.2 }}
            >
                Select
            </StyledLinkButton>
        );
    };

    const taskTotal =
        (Array.isArray(todoTasks) ? todoTasks.length : 0) +
        (Array.isArray(bugsTasks) ? bugsTasks.length : 0) +
        (Array.isArray(resolvedTasks) ? resolvedTasks.length : 0) +
        (Array.isArray(pmoTasks) ? pmoTasks.length : 0);

    const showDragPlaceholder = (columnId) => Boolean(draggingColumn && draggingColumn !== columnId);

    const dropZoneActive = (columnId) =>
        Boolean(draggingColumn && draggingColumn !== columnId && dropTarget === columnId);

    const multiDragActive = draggingSelectedCount > 1 && (draggingColumn === COL.TODO || draggingColumn === COL.RESOLVED);

    const renderTodoCard = (t, rowIndex) => {
        const apiId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
        const id = apiId;
        const listKey = `${String(sprintId ?? 's')}-qa-todo-${String(apiId ?? '')}-${rowIndex}`;
        const checked = apiId != null && todoPmoSelectedSet.has(String(apiId));
        const isDisabled = typeof isTodoPmoTaskDisabled === 'function' ? isTodoPmoTaskDisabled(t) : false;

        const activeDragTaskId = activeDragPayload?.taskId;
        const isDraggingThis = activeDragTaskId != null && String(activeDragTaskId) === String(apiId);
        const isPartofActiveDragBatch =
            todoPmoSelectMode &&
            activeDragTaskId != null &&
            todoPmoSelectedIds.map(String).includes(String(activeDragTaskId)) &&
            todoPmoSelectedIds.map(String).includes(String(apiId));

        const shouldCollapse = isDraggingThis || isPartofActiveDragBatch;

        if (todoPmoSelectMode) {
            return (
                <motion.div
                    key={listKey}
                    layout
                    initial={false}
                    animate={{
                        height: shouldCollapse ? 0 : 'auto',
                        opacity: shouldCollapse ? 0 : 1,
                    }}
                    transition={{
                        layout: {
                            type: 'spring',
                            stiffness: 150,
                            damping: 24,
                            mass: 1.0,
                        },
                        height: {
                            duration: 0.2,
                            ease: 'easeInOut',
                        },
                        opacity: {
                            duration: 0.15,
                        }
                    }}
                    style={{
                        width: '100%',
                        flexShrink: 0,
                        minWidth: 0,
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        overflow: 'hidden',
                    }}
                >
                    <Checkbox
                        checked={checked}
                        disabled={isDisabled}
                        onChange={() => onTodoPmoToggleTask?.(apiId)}
                        style={{ marginTop: 14, flexShrink: 0 }}
                        aria-label="Select task for PMO submission"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <DraggableTodoCard
                            task={t}
                            draggableId={`qa-todo-${String(id)}`}
                            selectedTaskIds={checked ? todoPmoSelectedIds.map(String) : undefined}
                            dragEnabled={Boolean(checked)}
                        >
                            <SprintTaskCard
                                task={t}
                                qaSubmissions={qaSubmissions}
                                variant="readOnly"
                                kanbanColumn={COL.TODO}
                                kanbanColumnTintOnly
                                isDisabled={isDisabled}
                                onSelect={
                                    !isDisabled && typeof onTodoPmoToggleTask === 'function' && apiId != null
                                        ? () => onTodoPmoToggleTask(apiId)
                                        : undefined
                                }
                            />
                        </DraggableTodoCard>
                    </div>
                </motion.div>
            );
        }

        return (
            <motion.div
                key={listKey}
                layout
                initial={false}
                animate={{
                    height: shouldCollapse ? 0 : 'auto',
                    opacity: shouldCollapse ? 0 : 1,
                }}
                transition={{
                    layout: {
                        type: 'spring',
                        stiffness: 150,
                        damping: 24,
                        mass: 1.0,
                    },
                    height: {
                        duration: 0.2,
                        ease: 'easeInOut',
                    },
                    opacity: {
                        duration: 0.15,
                    }
                }}
                style={{
                    width: '100%',
                    flexShrink: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                <DraggableTodoCard
                    task={t}
                    draggableId={`qa-todo-${String(id)}`}
                    selectedTaskIds={todoPmoSelectMode && checked ? todoPmoSelectedIds.map(String) : undefined}
                    dragEnabled
                >
                    <SprintTaskCard
                        task={t}
                        qaSubmissions={qaSubmissions}
                        variant="readOnly"
                        kanbanColumn={COL.TODO}
                        kanbanColumnTintOnly
                        onSelect={
                            onTaskSelect
                                ? () =>
                                    onTaskSelect(sprint, t, {
                                        readOnlyBugActions: false,
                                        defaultBugsTab: true,
                                    })
                                : undefined
                        }
                    />
                </DraggableTodoCard>
            </motion.div>
        );
    };

    const renderResolvedCard = (t, rowIndex) => {
        const apiId = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
        const id = apiId;
        const listKey = `${String(sprintId ?? 's')}-qa-resolved-${String(apiId ?? '')}-${rowIndex}`;
        const checked = apiId != null && resolvedPmoSelectedSet.has(String(apiId));
        const isDisabled = typeof isResolvedPmoTaskDisabled === 'function' ? isResolvedPmoTaskDisabled(t) : false;

        const activeDragTaskId = activeDragPayload?.taskId;
        const isDraggingThis = activeDragTaskId != null && String(activeDragTaskId) === String(apiId);
        const isPartofActiveDragBatch =
            resolvedPmoSelectMode &&
            activeDragTaskId != null &&
            resolvedPmoSelectedIds.map(String).includes(String(activeDragTaskId)) &&
            resolvedPmoSelectedIds.map(String).includes(String(apiId));

        const shouldCollapse = isDraggingThis || isPartofActiveDragBatch;

        if (resolvedPmoSelectMode) {
            return (
                <motion.div
                    key={listKey}
                    layout
                    initial={false}
                    animate={{
                        height: shouldCollapse ? 0 : 'auto',
                        opacity: shouldCollapse ? 0 : 1,
                    }}
                    transition={{
                        layout: {
                            type: 'spring',
                            stiffness: 150,
                            damping: 24,
                            mass: 1.0,
                        },
                        height: {
                            duration: 0.2,
                            ease: 'easeInOut',
                        },
                        opacity: {
                            duration: 0.15,
                        }
                    }}
                    style={{
                        width: '100%',
                        flexShrink: 0,
                        minWidth: 0,
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        overflow: 'hidden',
                    }}
                >
                    <Checkbox
                        checked={checked}
                        disabled={isDisabled}
                        onChange={() => onResolvedPmoToggleTask?.(apiId)}
                        style={{ marginTop: 14, flexShrink: 0 }}
                        aria-label="Select task for PMO submission"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <DraggableResolvedCard
                            task={t}
                            draggableId={`qa-resolved-${String(id)}`}
                            selectedTaskIds={checked ? resolvedPmoSelectedIds.map(String) : undefined}
                            dragEnabled={Boolean(checked)}
                        >
                            <SprintTaskCard
                                task={t}
                                qaSubmissions={qaSubmissions}
                                variant="readOnly"
                                kanbanColumn={COL.RESOLVED}
                                kanbanColumnTintOnly
                                isDisabled={isDisabled}
                                onSelect={
                                    !isDisabled && typeof onResolvedPmoToggleTask === 'function' && apiId != null
                                        ? () => onResolvedPmoToggleTask(apiId)
                                        : undefined
                                }
                            />
                        </DraggableResolvedCard>
                    </div>
                </motion.div>
            );
        }

        return (
            <motion.div
                key={listKey}
                layout
                initial={false}
                animate={{
                    height: shouldCollapse ? 0 : 'auto',
                    opacity: shouldCollapse ? 0 : 1,
                }}
                transition={{
                    layout: {
                        type: 'spring',
                        stiffness: 150,
                        damping: 24,
                        mass: 1.0,
                    },
                    height: {
                        duration: 0.2,
                        ease: 'easeInOut',
                    },
                    opacity: {
                        duration: 0.15,
                    }
                }}
                style={{
                    width: '100%',
                    flexShrink: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                <DraggableResolvedCard
                    task={t}
                    draggableId={`qa-resolved-${String(id)}`}
                    selectedTaskIds={resolvedPmoSelectMode && checked ? resolvedPmoSelectedIds.map(String) : undefined}
                    dragEnabled
                >
                    <SprintTaskCard
                        task={t}
                        qaSubmissions={qaSubmissions}
                        variant="readOnly"
                        kanbanColumn={COL.RESOLVED}
                        kanbanColumnTintOnly
                        onSelect={
                            onTaskSelect
                                ? () =>
                                    onTaskSelect(sprint, t, {
                                        readOnlyBugActions: false,
                                        defaultBugsTab: true,
                                    })
                                : undefined
                        }
                    />
                </DraggableResolvedCard>
            </motion.div>
        );
    };

    const renderTodoSubmissionGroupCard = (group, groupIndex) => {
        const gid =
            group?.id ??
            group?.submissionId ??
            group?._id ??
            `${String(sprintId ?? 's')}-qa-submission-${groupIndex}`;

        const listKey = `${String(sprintId ?? 's')}-qa-submission-${String(gid)}`;

        const submissionTasks = Array.isArray(group?.tasks) ? group.tasks : [];

        return (
            <DraggableQaSubmissionGroupCard
                key={listKey}
                sprintId={sprintId}
                group={group}
                groupIndex={groupIndex}
                todoPmoSelectMode={todoPmoSelectMode}
            >
                {({ submissionDragZoneProps }) => (
                    <QASubmissionGroupCard
                        sidLabel={group?.sidLabel}
                        submissionTitle={group?.submissionTitle}
                        submissionStatus={group?.statusLabel}
                        descriptionMarkdown={group?.descriptionMarkdown}
                        descriptionMentions={group?.descriptionMentions}
                        attachmentCount={group?.attachmentCount}
                        submittedAtLabel={group?.submittedAtLabel}
                        submittedBy={group?.submittedBy}
                        submissionDragZoneProps={submissionDragZoneProps}
                        tasks={submissionTasks}
                        onTaskClick={(t) =>
                            onTaskSelect?.(sprint, t, {
                                readOnlyBugActions: false,
                                defaultBugsTab: true,
                            })
                        }
                        onOpenDetail={(opts) => onSubmissionOpen?.(sprint, group, opts)}
                        decorateTaskRow={(task, _idx, row) => {
                            const apiId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
                            const id = apiId;
                            const checked = apiId != null && todoPmoSelectedSet.has(String(apiId));

                            if (todoPmoSelectMode) {
                                return (
                                    <div
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            gap: 10,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onChange={() => onTodoPmoToggleTask?.(apiId)}
                                            style={{ marginTop: 8, flexShrink: 0 }}
                                            aria-label="Select task for PMO submission"
                                        />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <DraggableTodoCard
                                                task={task}
                                                draggableId={`qa-todo-${String(id)}`}
                                                selectedTaskIds={checked ? todoPmoSelectedIds.map(String) : undefined}
                                                dragEnabled={Boolean(checked)}
                                            >
                                                {row}
                                            </DraggableTodoCard>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <DraggableTodoCard
                                    task={task}
                                    draggableId={`qa-todo-${String(id)}`}
                                    selectedTaskIds={undefined}
                                    dragEnabled
                                >
                                    {row}
                                </DraggableTodoCard>
                            );
                        }}
                    />
                )}
            </DraggableQaSubmissionGroupCard>
        );
    };

    const renderPmoSubmissionGroupCard = (group, groupIndex) => {
        const gid =
            group?.id ??
            group?.submissionId ??
            group?._id ??
            `${String(sprintId ?? 's')}-pmo-submission-${groupIndex}`;

        const listKey = `${String(sprintId ?? 's')}-pmo-submission-${String(gid)}`;

        const submissionTasks = Array.isArray(group?.tasks) ? group.tasks : [];

        return (
            <QASubmissionGroupCard
                key={listKey}
                sidLabel={group?.sidLabel}
                submissionTitle={group?.submissionTitle}
                submissionStatus={group?.statusLabel}
                descriptionMarkdown={group?.descriptionMarkdown}
                descriptionMentions={group?.descriptionMentions}
                attachmentCount={group?.attachmentCount}
                submittedAtLabel={group?.submittedAtLabel}
                submittedBy={group?.submittedBy}
                tasks={submissionTasks}
                onTaskClick={(t) =>
                    onPmoSubmittedTaskOpenDetail
                        ? onPmoSubmittedTaskOpenDetail(sprint, t)
                        : onTaskSelect?.(sprint, t, {
                            readOnlyBugActions: true,
                            defaultBugsTab: false,
                        })
                }
                onOpenDetail={() => onSubmissionOpen?.(sprint, group)}
            />
        );
    };

    const renderStaticCard = (t, column, kanbanColumnProp) => {
        const key = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
        const bugs = column === COL.BUGS ? t?.bugs ?? [] : undefined;
        const isSubmittedPmoColumn = kanbanColumnProp === 'submitted';
        return (
            <SprintTaskCard
                key={key}
                task={t}
                variant="readOnly"
                kanbanColumn={kanbanColumnProp}
                kanbanColumnTintOnly
                bugs={bugs}
                onOpenDetail={
                    isSubmittedPmoColumn && onPmoSubmittedTaskOpenDetail
                        ? (taskRow) => onPmoSubmittedTaskOpenDetail(sprint, taskRow)
                        : undefined
                }
                onSelect={
                    !isSubmittedPmoColumn && onTaskSelect
                        ? () =>
                            onTaskSelect(
                                sprint,
                                t,
                                kanbanColumnProp === 'bugs'
                                    ? { readOnlyBugActions: true, defaultBugsTab: false }
                                    : undefined,
                            )
                        : undefined
                }
            />
        );
    };

    const kanbanColumns = isNarrow ? (
        <KanbanThreeColumnRow className="sprint-kanban-column-row-natural" style={{ marginTop: 0 }}>
            <KanbanColumnPanel data-kanban-drop-column={COL.TODO}>
                <KanbanColumnHeader style={{ width: '100%' }}>
                    <KanbanColumnTitle>TODOs</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.TODO}>{filteredTodoTasks.length}</KanbanColumnCountBadge>
                    {renderTodoHeaderRight()}
                </KanbanColumnHeader>
                <DroppableKanbanColumnBody
                    columnId={COL.TODO}
                    className="sprint-kanban-column-body-natural"
                    $qaStack
                    data-drag-placeholder={showDragPlaceholder(COL.TODO) ? 'true' : undefined}
                    $dragPlaceholder={showDragPlaceholder(COL.TODO)}
                >
                    {showDragPlaceholder(COL.TODO) ? (
                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.TODO)}>
                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                            <span>{QA_COLUMN_DROP_MESSAGES[COL.TODO]}</span>
                        </KanbanDragDropPlaceholder>
                    ) : (filteredTodoTasks.length === 0 && todoSubmissionGroups.length === 0) ? (
                        <KanbanEmptyColumn title="Todo" token={token} hasSprint={hasSprint} />
                    ) : (
                        Array.isArray(todoSubmissionGroups) && todoSubmissionGroups.length > 0 ? (
                            <>
                                {todoSubmissionGroups.slice(0, todoLimit).map((g, i) => renderTodoSubmissionGroupCard(g, i))}
                                {todoSubmissionGroups.length > todoLimit && (
                                    <LoadMoreCard onClick={() => setTodoLimit(l => l + 100)}>
                                        Load More Submissions (+{todoSubmissionGroups.length - todoLimit} remaining)
                                    </LoadMoreCard>
                                )}
                            </>
                        ) : (
                            <>
                                {filteredTodoTasks.slice(0, todoLimit).map((t, i) => renderTodoCard(t, i))}
                                {filteredTodoTasks.length > todoLimit && (
                                    <LoadMoreCard onClick={() => setTodoLimit(l => l + 100)}>
                                        Load More Tasks (+{filteredTodoTasks.length - todoLimit} remaining)
                                    </LoadMoreCard>
                                )}
                            </>
                        )
                    )}
                </DroppableKanbanColumnBody>
            </KanbanColumnPanel>

            <KanbanColumnPanel data-kanban-drop-column={COL.BUGS}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Bugs Reported</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.BUGS} aria-label={`${bugsTasks.length} bugs`}>
                        {bugsTasks.length}
                    </KanbanColumnCountBadge>
                </KanbanColumnHeader>
                <DroppableKanbanColumnBody
                    columnId={COL.BUGS}
                    className="sprint-kanban-column-body-natural"
                    $qaStack
                    data-drag-placeholder={showDragPlaceholder(COL.BUGS) ? 'true' : undefined}
                    $dragPlaceholder={showDragPlaceholder(COL.BUGS)}
                >
                    {showDragPlaceholder(COL.BUGS) ? (
                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.BUGS)}>
                            {multiDragActive ? (
                                <>
                                    <StopOutlined style={{ fontSize: 22, opacity: 0.9 }} aria-hidden />
                                    <span>Multiple tasks can only drop in Submitted (PMO)</span>
                                </>
                            ) : (
                                <>
                                    <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                    <span>{QA_COLUMN_DROP_MESSAGES[COL.BUGS]}</span>
                                </>
                            )}
                        </KanbanDragDropPlaceholder>
                    ) : bugsTasks.length === 0 ? (
                        <KanbanEmptyColumn title="Bugs Reported" token={token} hasSprint={hasSprint} />
                    ) : (
                        <>
                            {bugsTasks.slice(0, bugsLimit).map((t) => renderStaticCard(t, COL.BUGS, COL.BUGS))}
                            {bugsTasks.length > bugsLimit && (
                                <LoadMoreCard onClick={() => setBugsLimit(l => l + 100)}>
                                    Load More Bugs (+{bugsTasks.length - bugsLimit} remaining)
                                </LoadMoreCard>
                            )}
                        </>
                    )}
                </DroppableKanbanColumnBody>
            </KanbanColumnPanel>

            <KanbanColumnPanel data-kanban-drop-column={COL.RESOLVED}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Resolved</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant={COL.RESOLVED}>{filteredResolvedTasks.length}</KanbanColumnCountBadge>
                    {renderResolvedHeaderRight()}
                </KanbanColumnHeader>
                <DroppableKanbanColumnBody
                    columnId={COL.RESOLVED}
                    className="sprint-kanban-column-body-natural"
                    $qaStack
                    data-drag-placeholder={showDragPlaceholder(COL.RESOLVED) ? 'true' : undefined}
                    $dragPlaceholder={showDragPlaceholder(COL.RESOLVED)}
                >
                    {showDragPlaceholder(COL.RESOLVED) ? (
                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.RESOLVED)}>
                            {multiDragActive ? (
                                <>
                                    <StopOutlined style={{ fontSize: 22, opacity: 0.9 }} aria-hidden />
                                    <span>Multiple tasks can only drop in Submitted (PMO)</span>
                                </>
                            ) : (
                                <>
                                    <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                    <span>{QA_COLUMN_DROP_MESSAGES[COL.RESOLVED]}</span>
                                </>
                            )}
                        </KanbanDragDropPlaceholder>
                    ) : filteredResolvedTasks.length === 0 ? (
                        <KanbanEmptyColumn title="Resolved" token={token} hasSprint={hasSprint} />
                    ) : (
                        <>
                            {filteredResolvedTasks.slice(0, resolvedLimit).map((t, i) => renderResolvedCard(t, i))}
                            {filteredResolvedTasks.length > resolvedLimit && (
                                <LoadMoreCard onClick={() => setResolvedLimit(l => l + 100)}>
                                    Load More Tasks (+{filteredResolvedTasks.length - resolvedLimit} remaining)
                                </LoadMoreCard>
                            )}
                        </>
                    )}
                </DroppableKanbanColumnBody>
            </KanbanColumnPanel>

            <KanbanColumnPanel data-kanban-drop-column={COL.PMO}>
                <KanbanColumnHeader>
                    <KanbanColumnTitle>Submitted</KanbanColumnTitle>
                    <KanbanColumnCountBadge $variant="submitted">{pmoTasks.length}</KanbanColumnCountBadge>
                </KanbanColumnHeader>
                <DroppableKanbanColumnBody
                    columnId={COL.PMO}
                    className="sprint-kanban-column-body-natural"
                    $qaStack
                    data-drag-placeholder={showDragPlaceholder(COL.PMO) ? 'true' : undefined}
                    $dragPlaceholder={showDragPlaceholder(COL.PMO)}
                >
                    {showDragPlaceholder(COL.PMO) ? (
                        <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.PMO)}>
                            <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                            <span>{QA_COLUMN_DROP_MESSAGES[COL.PMO]}</span>
                        </KanbanDragDropPlaceholder>
                    ) : (pmoTasks.length === 0 && pmoSubmissionGroups.length === 0) ? (
                        <KanbanEmptyColumn title="Submitted" token={token} hasSprint={hasSprint} />
                    ) : (
                        Array.isArray(pmoSubmissionGroups) && pmoSubmissionGroups.length > 0 ? (
                            <>
                                {pmoSubmissionGroups.slice(0, pmoLimit).map((g, i) => renderPmoSubmissionGroupCard(g, i))}
                                {pmoSubmissionGroups.length > pmoLimit && (
                                    <LoadMoreCard onClick={() => setPmoLimit(l => l + 100)}>
                                        Load More Submissions (+{pmoSubmissionGroups.length - pmoLimit} remaining)
                                    </LoadMoreCard>
                                )}
                            </>
                        ) : (
                            <>
                                {pmoTasks.slice(0, pmoLimit).map((t) => renderStaticCard(t, COL.PMO, 'submitted'))}
                                {pmoTasks.length > pmoLimit && (
                                    <LoadMoreCard onClick={() => setPmoLimit(l => l + 100)}>
                                        Load More Tasks (+{pmoTasks.length - pmoLimit} remaining)
                                    </LoadMoreCard>
                                )}
                            </>
                        )
                    )}
                </DroppableKanbanColumnBody>
            </KanbanColumnPanel>
        </KanbanThreeColumnRow>
    ) : (
        <div className="sprint-kanban-desktop-split">
            <div data-sprint-kanban-sticky-strip className="sprint-kanban-sticky-strip">
                <div ref={headerScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onHeaderScroll}>
                    <div style={kanbanDesktopBoardHeaderRowInnerStyle}>
                        <div style={kanbanDesktopHeaderCellFlexStyle} data-kanban-drop-column={COL.TODO}>
                            <KanbanColumnHeader style={{ width: '100%' }}>
                                <KanbanColumnTitle>TODOs</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.TODO}>{filteredTodoTasks.length}</KanbanColumnCountBadge>
                                {renderTodoHeaderRight()}
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle}>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Bugs Reported</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.BUGS} aria-label={`${bugsTasks.length} bugs`}>
                                    {bugsTasks.length}
                                </KanbanColumnCountBadge>
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle} data-kanban-drop-column={COL.RESOLVED}>
                            <KanbanColumnHeader style={{ width: '100%' }}>
                                <KanbanColumnTitle>Resolved</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant={COL.RESOLVED}>{filteredResolvedTasks.length}</KanbanColumnCountBadge>
                                {renderResolvedHeaderRight()}
                            </KanbanColumnHeader>
                        </div>
                        <div style={kanbanDesktopHeaderCellFlexStyle}>
                            <KanbanColumnHeader>
                                <KanbanColumnTitle>Submitted</KanbanColumnTitle>
                                <KanbanColumnCountBadge $variant="submitted">{pmoTasks.length}</KanbanColumnCountBadge>
                            </KanbanColumnHeader>
                        </div>
                    </div>
                </div>
            </div>
            <div className="sprint-kanban-desktop-body-wrap">
                <div ref={bodyScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onBodyScroll}>
                    <div className="sprint-kanban-desktop-board-row" style={kanbanDesktopBoardRowInnerStyle}>
                        <KanbanColumnPanel data-kanban-drop-column={COL.TODO}>
                            <DroppableKanbanColumnBody
                                columnId={COL.TODO}
                                className="sprint-kanban-column-body-natural"
                                $qaStack
                                data-drag-placeholder={showDragPlaceholder(COL.TODO) ? 'true' : undefined}
                                $dragPlaceholder={showDragPlaceholder(COL.TODO)}
                            >
                                {showDragPlaceholder(COL.TODO) ? (
                                    <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.TODO)}>
                                        <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                        <span>{QA_COLUMN_DROP_MESSAGES[COL.TODO]}</span>
                                    </KanbanDragDropPlaceholder>
                                ) : (filteredTodoTasks.length === 0 && todoSubmissionGroups.length === 0) ? (
                                    <KanbanEmptyColumn title="Todo" token={token} hasSprint={hasSprint} />
                                ) : (
                                    Array.isArray(todoSubmissionGroups) && todoSubmissionGroups.length > 0 ? (
                                        <>
                                            {todoSubmissionGroups.slice(0, todoLimit).map((g, i) => renderTodoSubmissionGroupCard(g, i))}
                                            {todoSubmissionGroups.length > todoLimit && (
                                                <LoadMoreCard onClick={() => setTodoLimit(l => l + 100)}>
                                                    Load More Submissions (+{todoSubmissionGroups.length - todoLimit} remaining)
                                                </LoadMoreCard>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {filteredTodoTasks.slice(0, todoLimit).map((t, i) => renderTodoCard(t, i))}
                                            {filteredTodoTasks.length > todoLimit && (
                                                <LoadMoreCard onClick={() => setTodoLimit(l => l + 100)}>
                                                    Load More Tasks (+{filteredTodoTasks.length - todoLimit} remaining)
                                                </LoadMoreCard>
                                            )}
                                        </>
                                    )
                                )}
                            </DroppableKanbanColumnBody>
                        </KanbanColumnPanel>

                        <KanbanColumnPanel data-kanban-drop-column={COL.BUGS}>
                            <DroppableKanbanColumnBody
                                columnId={COL.BUGS}
                                className="sprint-kanban-column-body-natural"
                                $qaStack
                                data-drag-placeholder={showDragPlaceholder(COL.BUGS) ? 'true' : undefined}
                                $dragPlaceholder={showDragPlaceholder(COL.BUGS)}
                            >
                                {showDragPlaceholder(COL.BUGS) ? (
                                    <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.BUGS)}>
                                        {multiDragActive ? (
                                            <>
                                                <StopOutlined style={{ fontSize: 22, opacity: 0.9 }} aria-hidden />
                                                <span>Multiple tasks can only drop in Submitted (PMO)</span>
                                            </>
                                        ) : (
                                            <>
                                                <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                                <span>{QA_COLUMN_DROP_MESSAGES[COL.BUGS]}</span>
                                            </>
                                        )}
                                    </KanbanDragDropPlaceholder>
                                ) : bugsTasks.length === 0 ? (
                                    <KanbanEmptyColumn title="Bugs Reported" token={token} hasSprint={hasSprint} />
                                ) : (
                                    <>
                                        {bugsTasks.slice(0, bugsLimit).map((t) => renderStaticCard(t, COL.BUGS, COL.BUGS))}
                                        {bugsTasks.length > bugsLimit && (
                                            <LoadMoreCard onClick={() => setBugsLimit(l => l + 100)}>
                                                Load More Bugs (+{bugsTasks.length - bugsLimit} remaining)
                                            </LoadMoreCard>
                                        )}
                                    </>
                                )}
                            </DroppableKanbanColumnBody>
                        </KanbanColumnPanel>

                        <KanbanColumnPanel data-kanban-drop-column={COL.RESOLVED}>
                            <DroppableKanbanColumnBody
                                columnId={COL.RESOLVED}
                                className="sprint-kanban-column-body-natural"
                                $qaStack
                                data-drag-placeholder={showDragPlaceholder(COL.RESOLVED) ? 'true' : undefined}
                                $dragPlaceholder={showDragPlaceholder(COL.RESOLVED)}
                            >
                                {showDragPlaceholder(COL.RESOLVED) ? (
                                    <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.RESOLVED)}>
                                        {multiDragActive ? (
                                            <>
                                                <StopOutlined style={{ fontSize: 22, opacity: 0.9 }} aria-hidden />
                                                <span>Multiple tasks can only drop in Submitted (PMO)</span>
                                            </>
                                        ) : (
                                            <>
                                                <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                                <span>{QA_COLUMN_DROP_MESSAGES[COL.RESOLVED]}</span>
                                            </>
                                        )}
                                    </KanbanDragDropPlaceholder>
                                ) : filteredResolvedTasks.length === 0 ? (
                                    <KanbanEmptyColumn title="Resolved" token={token} hasSprint={hasSprint} />
                                ) : (
                                    <>
                                        {filteredResolvedTasks.slice(0, resolvedLimit).map((t, i) => renderResolvedCard(t, i))}
                                        {filteredResolvedTasks.length > resolvedLimit && (
                                            <LoadMoreCard onClick={() => setResolvedLimit(l => l + 100)}>
                                                Load More Tasks (+{filteredResolvedTasks.length - resolvedLimit} remaining)
                                            </LoadMoreCard>
                                        )}
                                    </>
                                )}
                            </DroppableKanbanColumnBody>
                        </KanbanColumnPanel>

                        <KanbanColumnPanel data-kanban-drop-column={COL.PMO}>
                            <DroppableKanbanColumnBody
                                columnId={COL.PMO}
                                className="sprint-kanban-column-body-natural"
                                $qaStack
                                data-drag-placeholder={showDragPlaceholder(COL.PMO) ? 'true' : undefined}
                                $dragPlaceholder={showDragPlaceholder(COL.PMO)}
                            >
                                {showDragPlaceholder(COL.PMO) ? (
                                    <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(COL.PMO)}>
                                        <DragOutlined style={{ fontSize: 26, opacity: 0.88 }} aria-hidden />
                                        <span>{QA_COLUMN_DROP_MESSAGES[COL.PMO]}</span>
                                    </KanbanDragDropPlaceholder>
                                ) : (standalonePmoTasks.length === 0 && pmoSubmissionGroups.length === 0) ? (
                                    <KanbanEmptyColumn title="Submitted" token={token} hasSprint={hasSprint} />
                                ) : (
                                    <>
                                        {Array.isArray(pmoSubmissionGroups) && pmoSubmissionGroups.length > 0 && (
                                            <>
                                                {pmoSubmissionGroups.slice(0, pmoLimit).map((g, i) => renderPmoSubmissionGroupCard(g, i))}
                                                {pmoSubmissionGroups.length > pmoLimit && (
                                                    <LoadMoreCard onClick={() => setPmoLimit(l => l + 100)}>
                                                        Load More Submissions (+{pmoSubmissionGroups.length - pmoLimit} remaining)
                                                    </LoadMoreCard>
                                                )}
                                            </>
                                        )}
                                        {Array.isArray(standalonePmoTasks) && standalonePmoTasks.length > 0 && (
                                            <>
                                                {standalonePmoTasks.slice(0, pmoLimit).map((t) => renderStaticCard(t, COL.PMO, 'submitted'))}
                                                {standalonePmoTasks.length > pmoLimit && (
                                                    <LoadMoreCard onClick={() => setPmoLimit(l => l + 100)}>
                                                        Load More Tasks (+{standalonePmoTasks.length - pmoLimit} remaining)
                                                    </LoadMoreCard>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </DroppableKanbanColumnBody>
                        </KanbanColumnPanel>
                    </div>
                </div>
            </div>
        </div>
    );

    const kanbanRow = <SprintKanbanFill className="sprint-kanban-fill-natural">{kanbanColumns}</SprintKanbanFill>;

    return (
        <Container
            className="qa-kanban-root sprint-kanban-root"
            $display="flex"
            $flexDirection="column"
            $flex="1"
            $minHeight="0"
            $gap="0"
            $overflow="hidden"
        >
            <SprintKanbanDragCursorStyle />
            <SprintKanbanScrollChainStyle />
            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {kanbanRow}
                <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
                    {activeDragPayload?.type === 'task' && activeDragPayload?.taskId ? (
                        (() => {
                            const task = activeDragPayload.sourceColumn === COL.RESOLVED
                                ? resolveDraggingResolvedTask(activeDragPayload.taskId)
                                : resolveDraggingTodoTask(activeDragPayload.taskId);
                            const selectedIds = activeDragPayload.selectedTaskIds;
                            const isStack = Array.isArray(selectedIds) && selectedIds.length > 1;
                            const stackCount = isStack ? selectedIds.length : 1;

                            if (isStack) {
                                return (
                                    <div style={{
                                        position: 'relative',
                                        width: 360,
                                        maxWidth: 420,
                                        pointerEvents: 'none',
                                    }}>
                                        {/* Bottom Card Stack Shadow */}
                                        <div style={{
                                            position: 'absolute',
                                            inset: '0 4px',
                                            background: 'var(--ant-color-bg-container, #ffffff)',
                                            borderRadius: '12px',
                                            border: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
                                            transform: 'translateY(12px) scale(0.95)',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                            zIndex: 1,
                                        }} />
                                        {/* Middle Card Stack Shadow */}
                                        <div style={{
                                            position: 'absolute',
                                            inset: '0 2px',
                                            background: 'var(--ant-color-bg-container, #ffffff)',
                                            borderRadius: '12px',
                                            border: '1px solid var(--ant-color-border-secondary, rgba(0, 0, 0, 0.06))',
                                            transform: 'translateY(6px) scale(0.975)',
                                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
                                            zIndex: 2,
                                        }} />
                                        {/* Top Card */}
                                        <div style={{
                                            position: 'relative',
                                            zIndex: 3,
                                            transform: 'scale(1.02)',
                                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
                                        }}>
                                            <SprintTaskCard
                                                task={task}
                                                variant="readOnly"
                                                draggable={false}
                                                kanbanColumnTintOnly
                                                kanbanColumn={activeDragPayload.sourceColumn === COL.RESOLVED ? COL.RESOLVED : COL.TODO}
                                                suppressOverflowMenu
                                            />
                                            {/* Stack count badge */}
                                            <div style={{
                                                position: 'absolute',
                                                right: '12px',
                                                bottom: '12px',
                                                minWidth: '22px',
                                                height: '22px',
                                                padding: '0 6px',
                                                borderRadius: '999px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                color: '#fff',
                                                background: 'var(--ant-color-primary, #7c3aed)',
                                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
                                                zIndex: 5,
                                            }}>
                                                {stackCount}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            // Single Card
                            return (
                                <div style={{
                                    width: 360,
                                    maxWidth: 420,
                                    opacity: 1,
                                    transform: 'scale(1.02)',
                                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.15)',
                                    pointerEvents: 'none',
                                }}>
                                    <SprintTaskCard
                                        task={task}
                                        variant="readOnly"
                                        draggable={false}
                                        kanbanColumnTintOnly
                                        kanbanColumn={activeDragPayload.sourceColumn === COL.RESOLVED ? COL.RESOLVED : COL.TODO}
                                        suppressOverflowMenu
                                    />
                                </div>
                            );
                        })()
                    ) : activeDragPayload?.type === 'submission' ? (
                        <div style={{ width: 420, maxWidth: '92vw' }}>
                            <QASubmissionGroupCard
                                sidLabel={submissionOverlay?.sidLabel}
                                submissionTitle={submissionOverlay?.submissionTitle}
                                submissionStatus={submissionOverlay?.submissionStatus}
                                descriptionMarkdown={submissionOverlay?.descriptionMarkdown}
                                descriptionMentions={submissionOverlay?.descriptionMentions}
                                attachmentCount={submissionOverlay?.attachmentCount}
                                submittedAtLabel={submissionOverlay?.submittedAtLabel}
                                submittedBy={submissionOverlay?.submittedBy}
                                tasks={submissionOverlay?.tasks ?? []}
                                overlay
                                onOpenDetail={null}
                                onTaskClick={null}
                            />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
            {todoPmoSelectMode && (
                <FloatingSelectionBar
                    selectedCount={todoPmoSelectedIds.length}
                    onSubmitSelection={onTodoPmoSubmitSelection}
                    onSubmitAll={onTodoPmoSubmitAll}
                    onCancel={onTodoPmoCancelSelectMode}
                    submitAllLabel="Submit All Todos"
                    totalCount={todoTasks.length}
                />
            )}

            {resolvedPmoSelectMode && (
                <FloatingSelectionBar
                    selectedCount={resolvedPmoSelectedIds.length}
                    onSubmitSelection={onResolvedPmoSubmitSelection}
                    onSubmitAll={onResolvedPmoSubmitAll}
                    onCancel={onResolvedPmoCancelSelectMode}
                    submitAllLabel="Submit All Resolved"
                    totalCount={resolvedTasks.length}
                />
            )}
        </Container>
    );
}
